<template>
    <div class="quickpay-payment-header" :class="{ 'quickpay-payment-header--with-logo': hasLogo }">
        <div v-if="hasLogo" class="quickpay-payment-header__logo-wrapper">
            <img :src="logoUrl" :alt="companyName" class="quickpay-payment-header__logo" />
        </div>
        <div class="quickpay-payment-header__card">
            <div class="quickpay-payment-header__wrapper">
                <div
                    class="quickpay-payment-header__label typographies-paragraph-caps-small-semibold"
                >
                    {{ translations.companyLabel }}
                </div>
                <div class="quickpay-payment-header__company typographies-paragraph-large-semibold">
                    {{ companyName }}
                </div>
            </div>
            <div class="quickpay-payment-header__amount typographies-heading-h1">
                <span v-if="isCurrencyBefore" class="quickpay-payment-header__amount-currency">{{
                    currencySymbol
                }}</span>
                <span class="quickpay-payment-header__amount-value">{{ amountValue }}</span>
                <span v-if="!isCurrencyBefore" class="quickpay-payment-header__amount-currency">{{
                    currencySymbol
                }}</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        companyName: {
            type: String,
            required: true,
        },
        amount: {
            type: [Number, String],
            required: true,
        },
        currency: {
            type: String,
            default: 'EUR',
        },
        locale: {
            type: String,
            default: 'fr-FR',
        },
        logoUrl: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            translations: {
                companyLabel: this.$t.__('common.labels.company').toUpperCase(),
            },
        };
    },
    computed: {
        hasLogo() {
            return !!this.logoUrl;
        },
        amountAsNumber() {
            return parseFloat(this.amount);
        },
        amountValue() {
            if (isNaN(this.amountAsNumber)) {
                return this.amount;
            }

            const parts = new Intl.NumberFormat(this.locale, {
                style: 'currency',
                currency: this.currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).formatToParts(this.amountAsNumber);

            return parts
                .filter((part) => part.type !== 'currency')
                .map((part) => part.value)
                .join('');
        },
        currencySymbol() {
            if (isNaN(this.amountAsNumber)) {
                return '';
            }

            const parts = new Intl.NumberFormat(this.locale, {
                style: 'currency',
                currency: this.currency,
            }).formatToParts(0);

            const currencyPart = parts.find((part) => part.type === 'currency');
            return currencyPart ? currencyPart.value : this.currency;
        },
        isCurrencyBefore() {
            if (isNaN(this.amountAsNumber)) {
                return false;
            }

            const parts = new Intl.NumberFormat(this.locale, {
                style: 'currency',
                currency: this.currency,
            }).formatToParts(0);

            const currencyIndex = parts.findIndex((part) => part.type === 'currency');
            const integerIndex = parts.findIndex((part) => part.type === 'integer');
            return currencyIndex < integerIndex;
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-payment-header {
    position: relative;
    width: 100%;
    min-height: 110px;
    display: flex;
    flex-direction: column;
    border-radius: var(--spacing-1);
    padding: var(--spacing-5);
    margin: var(--spacing-5) 0 0;
    background: url(../ressources/logged/img/illustrations/payment/background-payment-header-company.svg) center center/cover no-repeat;
    color: var(--colors-common-text-standard-on-color);

    &__logo-wrapper {
        position: absolute;
        top: -25%;
        left: 50%;
        transform: translateX(-50%);
        background: var(--colors-common-background-default);
        border-radius: var(--spacing-2);
    }
    &__logo {
        max-width: 135px;
        height: 70px;
        margin: 0 45px;
    }
    &__card {
        flex: 1;
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        gap: var(--spacing-2);
        min-width: 0;
    }
    &--with-logo &__card {
        margin-top: 30px;
    }
    &__wrapper {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-0);
        justify-content: center;
        flex: 1 1 0;
        min-width: 0;
        overflow: hidden;
    }
    &__company {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    &__amount {
        display: flex;
        align-items: flex-end;
        justify-content: flex-end;
        gap: var(--spacing-0);
        text-align: right;
        flex: 1 1 0;
        min-width: 0;
        font-weight: 700;
    }
    &__amount-value {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
    }
    &__amount-currency {
        flex-shrink: 0;
    }

    @media (max-width: 1023px) {
        &__card {
            flex-direction: column;
            align-items: center;
            gap: var(--spacing-1);
        }
        &__wrapper {
            flex: 0 0 auto;
            max-width: 100%;
            align-items: center;
        }
        &__label {
            text-align: center;
        }
        &__company {
            white-space: normal;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            line-clamp: 2;
            text-align: center;
        }
        &__amount {
            flex: 0 0 auto;
            text-align: center;
            max-width: 100%;
            justify-content: center;
        }
    }
}
</style>
